import styled from 'styled-components'
import { WINDOW_SIZE } from '../util/Constants'

type PillTone = 'green' | 'amber' | 'red' | 'blue' | 'neutral'

const pillColors: Record<PillTone, { background: string, color: string }> = {
  green: { background: '#e8f7ef', color: '#16724b' },
  amber: { background: '#fff3df', color: '#a85014' },
  red: { background: '#ffe9ec', color: '#c51735' },
  blue: { background: '#edf3ff', color: '#3157c6' },
  neutral: { background: '#f3f5f9', color: '#687183' }
}

export const DashboardPage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  width: 100%;
`

export const ListPage = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 20px;
  min-width: 0;
  width: 100%;
`

export const PageHeaderRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    flex-direction: column;
  }
`

export const PageActionRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
`

export const PageHeaderStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`

export const ListControls = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

export const ListContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
`

export const ListTableSurface = styled.div`
  background: #fff;
  border: 1px solid #edf1f7;
  border-radius: 16px;
  min-width: 0;
  overflow: hidden;
`

export const ListPaginationRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

export const DropdownLabel = styled.div`
  color: #20242d;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.2;
`

export const InlineNotice = styled.div`
  align-items: center;
  color: #768092;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 8px;
  line-height: 1.45;
`

export const Surface = styled.section<{ $compact?: boolean }>`
  background: #fff;
  border: 1px solid #edf1f7;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => $compact ? '9px' : '12px'};
  min-width: 0;
  padding: ${({ $compact }) => $compact ? '12px' : '15px'};
`

export const SurfaceHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-width: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    flex-direction: column;
  }
`

export const SurfaceTitle = styled.h2`
  color: #20242d;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;
`

export const SurfaceSubtext = styled.p`
  color: #707887;
  font-size: 12px;
  line-height: 1.45;
  margin: 4px 0 0;
`

export const SummaryGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
`

export const SummaryTile = styled.div`
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 13px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding: 10px 12px;
`

export const SummaryStrip = styled.div`
  background: #eef2f7;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  overflow: hidden;
`

export const SummaryStripItem = styled.div`
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  padding: 10px 12px;
`

export const SummaryLabel = styled.span`
  color: #6f7888;
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.03em;
  line-height: 1.25;
  text-transform: uppercase;
`

export const SummaryValue = styled.span`
  color: #121722;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.12;
  overflow-wrap: anywhere;
`

export const SummaryMeta = styled.span`
  color: #7d8492;
  font-size: 11.5px;
  line-height: 1.35;
`

export const InlinePill = styled.span<{ $tone?: PillTone }>`
  align-items: center;
  background: ${({ $tone = 'neutral' }) => pillColors[$tone].background};
  border-radius: 999px;
  color: ${({ $tone = 'neutral' }) => pillColors[$tone].color};
  display: inline-flex;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1;
  padding: 4px 8px;
  white-space: nowrap;
`

export const EmptyState = styled.div`
  align-items: center;
  background: #f8fafc;
  border: 1px dashed #d9dee8;
  border-radius: 14px;
  color: #707887;
  display: flex;
  font-size: 13px;
  justify-content: center;
  line-height: 1.45;
  min-height: 112px;
  padding: 22px;
  text-align: center;
`

export const DetailGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 0.55fr);

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: 1fr;
  }
`
